<script setup lang="ts">
import { faLocationDot, type IconDefinition } from '@fortawesome/pro-light-svg-icons'

import type { Suggestion } from '@consumer/logic/v2/header'
import AutocompleteApi from '@consumer/api/AutocompleteApi'
import type { FeaturedCity } from '@consumer/types/serializers'
import { inject } from 'vue'

const props = defineProps<{
  input?: string
  showDefaultCities?: boolean
  disableHighlight?: boolean
}>()

type CitySuggestion = {
  label: string
  icon?: IconDefinition
  image?: string
  stateCode?: string
  city?: string
}

const apiSuggestions = ref<CitySuggestion[]>([])
watch(() => props.input, async () => {
  if (props.input) {
    const response = await AutocompleteApi.featuredCities({ query: { q: props.input } })
    apiSuggestions.value = response.map((suggestion: FeaturedCity) => {
      const city: CitySuggestion = {
        label: `${suggestion.name}, ${suggestion.stateCode}`,
        stateCode: suggestion.stateCode,
        city: suggestion.name,
      }

      if (suggestion.imageThumbUrl) {
        city.image = suggestion.imageThumbUrl
      }
      else {
        city.icon = faLocationDot
      }

      return city
    })

    emit('hasResults', response.length > 0)
  }
})

const topCities = inject<FeaturedCity[]>('topCities', [])
const defaultSuggestions: CitySuggestion[] = [
  {
    label: 'Nearby',
    icon: faLocationDot,
  },
  ...topCities.map((mappedCity) => {
    const city: CitySuggestion = {
      label: `${mappedCity.name}, ${mappedCity.stateCode}`,
      city: mappedCity.name,
    }

    if (mappedCity.imageThumbUrl) {
      city.image = mappedCity.imageThumbUrl
    }
    else {
      city.icon = faLocationDot
    }

    return city
  }),
]

const suggestions = computed(() => {
  if (props.input) {
    return apiSuggestions.value
  }
  else if (props.showDefaultCities) {
    return defaultSuggestions
  }
  else {
    return []
  }
})

const emit = defineEmits<{
  select: [value: Suggestion]
  hasResults: [value: boolean]
}>()

const selectSuggestion = (suggestion: Suggestion) => {
  emit('select', suggestion)
}

const highlightedIdx = ref(0)

function highlightNextSuggestion () {
  if (highlightedIdx.value < suggestions.value.length - 1) {
    highlightedIdx.value++
  }
}
function highlightPreviousSuggestion () {
  if (highlightedIdx.value > 0) {
    highlightedIdx.value--
  }
}
function highlightFirstSuggestion () {
  highlightedIdx.value = 0
}
function selectHighlightedSuggestion () {
  selectSuggestion(suggestions.value[highlightedIdx.value])
}
defineExpose({
  highlightNextSuggestion,
  highlightPreviousSuggestion,
  highlightFirstSuggestion,
  selectHighlightedSuggestion,
})

const isHighlighted = (index: number) => {
  return !props.disableHighlight && index === highlightedIdx.value
}
</script>

<template>
  <V2List
    v-if="!!suggestions.length"
    class="w-full"
    data-testid="suggestion-city-list"
  >
    <V2ListItem
      v-for="(suggestion, index) in suggestions"
      :key="suggestion.label"
      as="button"
      class="w-full cursor-pointer suggestion city"
      :highlighted="isHighlighted(index)"
      :label="suggestion.label"
      :icon="suggestion.icon"
      :image="suggestion.image"
      loading="lazy"
      data-testid="suggestion-city-item"
      @mousedown="selectSuggestion(suggestion)"
    />
  </V2List>
</template>
